/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  SafText,
  SafButton,
  SafTable,
  SafAnchor,
  SafAlert,
  SafCheckbox,
  SafPagination,
  SafDialog,
} from "@saffron/core-components/react";
import { useParams } from "react-router-dom";
import "../../App.css";
import "../../css/common.css";
import { callMiddleware } from "../../util/api";
import { SafDialogbox } from "../../shared";

const LucidChartImportTab = () => {
  const { assetId: urlAssetId } = useParams();
  const assetId = urlAssetId || "";

  const [diagrams, setDiagrams] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [alertMessage, setAlertMessage] = useState("");
  const [openAlertOptions, setOpenAlertOptions] = useState(undefined);

  // Per-diagram analysis state: { [diagramUuid]: { isAnalysing: boolean, lastAnalyzed: string, expanded: boolean } }
  const [diagramStates, setDiagramStates] = useState({});

  // Per-diagram technologies: { [diagramUuid]: { matchedTechnologies: [], unmatchedTechnologies: [] } }
  const [diagramTechnologies, setDiagramTechnologies] = useState({});

  // Per-diagram checkbox state: { [diagramUuid]: { [techKey]: { frontend, backend, devops, exclude } } }
  const [diagramCheckboxStates, setDiagramCheckboxStates] = useState({});

  // Per-diagram pagination state: { [diagramUuid]: { matched: {...}, unmatched: {...} } }
  const [diagramPaginationStates, setDiagramPaginationStates] = useState({});

  // Confirm import dialog
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedDiagramForImport, setSelectedDiagramForImport] =
    useState(null);

  // Add diagram dialog
  const [isDiagramDialogOpen, setIsDiagramDialogOpen] = useState(false);

  // Cloud provider filter - reads from same sessionStorage as Cloud Permit
  const [selectedCloudProvider, setSelectedCloudProvider] = useState(() => {
    return sessionStorage.getItem(`selectedCloudProvider_${assetId}`) || null;
  });

  const apiEndpoint = process.env.REACT_APP_MIDDLEWARE_ENDPOINT;
  const lucidEndpoint = process.env.REACT_APP_LUCID_SUMMARIZE_TECH_ENDPOINT;
  const dbName = process.env.REACT_APP_DB_TARGET;
  const containerName = process.env.REACT_APP_CONTAINER_ASSET_PARTITION;

  // Build flat list of all tech-stack-mapping services keyed by technology_name (lowercase)
  const buildTechLookup = (resources) => {
    const lookup = {};
    if (!Array.isArray(resources)) return lookup;
    resources.forEach((item) => {
      if (!Array.isArray(item.capabilities)) return;
      item.capabilities.forEach((cap) => {
        if (!Array.isArray(cap.services)) return;
        cap.services.forEach((svc) => {
          if (svc.technology_name) {
            lookup[svc.technology_name.toLowerCase()] = {
              tech_radar_id: svc.tech_radar_id || "",
              technologyName: svc.technology_name || "",
              category: item.category || "",
              capability: cap.capability || "",
              recommendation: svc.recommendation || "",
              provider: svc.provider || "",
            };
          }
        });
      });
    });
    return lookup;
  };

  const fetchSummaryAndMatch = useCallback(async () => {
    if (!assetId) return;
    try {
      // Fetch per-diagram summaries from the new database structure
      const summaryData = await callMiddleware("get", {
        dbName,
        containerName,
        query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'lucid_mcp_tech_summary'`,
        partitionKey: assetId,
      });

      const record = Array.isArray(summaryData) ? summaryData[0] : summaryData;

      // Initialize per-diagram states and technologies
      const newDiagramStates = {};
      const newDiagramTechnologies = {};
      const newDiagramCheckboxStates = {};
      const newDiagramPaginationStates = {};

      // Fetch tech-stack-mapping once
      const mappingResources = await callMiddleware("get", {
        dbName: process.env.REACT_APP_DB_SOURCE,
        containerName: "tech-stack-mapping",
        query: "SELECT * FROM c",
      });

      const techLookup = buildTechLookup(mappingResources);

      // Fetch actual technology-stacks to check presence
      const techStacksData = await callMiddleware("get", {
        dbName,
        containerName,
        query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'technology-stacks'`,
        partitionKey: assetId,
      });

      const techStacksRecord = Array.isArray(techStacksData)
        ? techStacksData[0]
        : techStacksData;

      // Build existing tech stacks lookup
      let techStackServices = new Set();
      let techStackServiceNames = new Set();

      if (
        techStacksRecord &&
        Array.isArray(techStacksRecord.technology_stacks)
      ) {
        techStacksRecord.technology_stacks.forEach((stack) => {
          if (Array.isArray(stack.tech_stack_environment)) {
            stack.tech_stack_environment.forEach((env) => {
              if (Array.isArray(env.services)) {
                env.services.forEach((service) => {
                  if (service.service_id) {
                    techStackServices.add(service.service_id);
                  }
                  if (service.service_name) {
                    techStackServiceNames.add(
                      service.service_name.toLowerCase()
                    );
                  }
                });
              }
            });
          }
        });
      }

      // Process each diagram's data from new structure: record.diagram_summaries
      if (record && record.diagram_summaries) {
        Object.entries(record.diagram_summaries).forEach(
          ([diagramUuid, diagramData]) => {
            // Initialize diagram state - preserve existing expanded state
            const currentState = diagramStates[diagramUuid];
            newDiagramStates[diagramUuid] = {
              isAnalysing: false,
              lastAnalyzed: diagramData.last_analyzed || null,
              expanded: currentState?.expanded || false,
            };

            // Process technologies for this diagram
            const technologiesData =
              diagramData?.lucid_summary?.combined_summary?.technologies;
            const matchedTechs = technologiesData?.matched_technologies || [];
            const unmatchedTechs =
              technologiesData?.unmatched_technologies || [];
            const excludedTechs = technologiesData?.excluded || [];

            // Process matched technologies with provider filtering
            const processedMatchedTechs = [];

            // Helper function to map provider names for comparison
            const normalizeProviderName = (provider) => {
              if (!provider) return "";
              const lowerProvider = provider.toLowerCase();
              if (
                lowerProvider.includes("aws") ||
                lowerProvider.includes("amazon")
              )
                return "AWS";
              if (
                lowerProvider.includes("azure") ||
                lowerProvider.includes("microsoft")
              )
                return "Azure";
              if (
                lowerProvider.includes("google") ||
                lowerProvider.includes("gcp") ||
                lowerProvider.includes("cloud")
              )
                return "GCP";
              if (
                lowerProvider.includes("oci") ||
                lowerProvider.includes("oracle")
              )
                return "OCI";
              return "Other";
            };

            // Determine which providers to show based on selected provider
            const getTargetProviders = (selectedProvider) => {
              if (!selectedProvider)
                return ["AWS", "Azure", "GCP", "OCI", "Other"]; // Show all if none selected

              // Normalize the selected provider to match our standard naming
              const normalizedSelected =
                normalizeProviderName(selectedProvider);
              return [normalizedSelected, "Other"];
            };

            const targetProviders = getTargetProviders(selectedCloudProvider);
            console.log(
              `[DEBUG] Selected provider: "${selectedCloudProvider}", Target providers: [${targetProviders.join(
                ", "
              )}]`
            );

            matchedTechs.forEach((providerGroup) => {
              // Debug logging for provider names
              if (providerGroup.provider) {
                console.log(
                  `[DEBUG] Original provider: "${
                    providerGroup.provider
                  }", Normalized: "${normalizeProviderName(
                    providerGroup.provider
                  )}"`
                );
              }

              // Handle both old structure (flat array) and new structure (provider-based)
              if (providerGroup.technology && providerGroup.service_id) {
                // Old structure - single technology object
                const serviceId = providerGroup.service_id;
                const technologyName = providerGroup.technology;

                if (!techStackServices.has(serviceId)) {
                  const entry = techLookup[technologyName.toLowerCase()];
                  processedMatchedTechs.push({
                    tech_radar_id: serviceId,
                    technologyName: technologyName,
                    category: entry?.category || "N/A",
                    capability: entry?.capability || "N/A",
                    recommendation: entry?.recommendation || "N/A",
                    provider: entry?.provider || "N/A",
                  });
                }
              } else if (
                providerGroup.provider &&
                Array.isArray(providerGroup.technologies)
              ) {
                // New structure - provider with technologies array
                const normalizedProvider = normalizeProviderName(
                  providerGroup.provider
                );

                // Only process if this provider should be shown
                console.log(
                  `[DEBUG] Provider "${
                    providerGroup.provider
                  }" normalized to "${normalizedProvider}", included: ${targetProviders.includes(
                    normalizedProvider
                  )}`
                );
                if (targetProviders.includes(normalizedProvider)) {
                  providerGroup.technologies.forEach((tech) => {
                    const serviceId = tech.service_id;
                    const technologyName = tech.technology;

                    if (!techStackServices.has(serviceId)) {
                      const entry = techLookup[technologyName.toLowerCase()];
                      processedMatchedTechs.push({
                        tech_radar_id: serviceId,
                        technologyName: technologyName,
                        category: entry?.category || "N/A",
                        capability: entry?.capability || "N/A",
                        recommendation: entry?.recommendation || "N/A",
                        provider: entry?.provider || "N/A",
                      });
                    }
                  });
                }
              }
            });

            // Process unmatched technologies
            const processedUnmatchedTechs = [];
            unmatchedTechs.forEach((tech) => {
              const technologyName = tech.technology || tech.original_extracted;

              if (!techStackServiceNames.has(technologyName.toLowerCase())) {
                processedUnmatchedTechs.push({
                  tech_radar_id: "",
                  technologyName: technologyName,
                  category: "Couldn't find in Tech Radar",
                  capability: "Couldn't find in Tech Radar",
                  recommendation: "Couldn't find in Tech Radar",
                  provider: "Couldn't find in Tech Radar",
                });
              }
            });

            // Process excluded technologies and add to appropriate tables
            excludedTechs.forEach((tech) => {
              const serviceId = tech.service_id;
              const technologyName = tech.technology || tech.original_extracted;

              if (serviceId) {
                // Excluded matched technology - add to matched table
                const entry = techLookup[technologyName.toLowerCase()];
                processedMatchedTechs.push({
                  tech_radar_id: serviceId,
                  technologyName: technologyName,
                  category: entry?.category || "N/A",
                  capability: entry?.capability || "N/A",
                  recommendation: entry?.recommendation || "N/A",
                  provider: entry?.provider || "N/A",
                });
              } else {
                // Excluded unmatched technology - add to unmatched table
                processedUnmatchedTechs.push({
                  tech_radar_id: "",
                  technologyName: technologyName,
                  category: "Couldn't find in Tech Radar",
                  capability: "Couldn't find in Tech Radar",
                  recommendation: "Couldn't find in Tech Radar",
                  provider: "Couldn't find in Tech Radar",
                });
              }
            });

            // Set technologies for this diagram
            newDiagramTechnologies[diagramUuid] = {
              matchedTechnologies: processedMatchedTechs,
              unmatchedTechnologies: processedUnmatchedTechs,
            };

            // Initialize checkbox states for this diagram
            const diagramCheckboxState = {};
            [...processedMatchedTechs, ...processedUnmatchedTechs].forEach(
              (tech) => {
                const key = tech.tech_radar_id || tech.technologyName;
                diagramCheckboxState[key] = {
                  frontend: false,
                  backend: false,
                  devops: false,
                  exclude: false,
                };
              }
            );

            // Mark excluded technologies
            excludedTechs.forEach((tech) => {
              const key =
                tech.service_id || tech.technology || tech.original_extracted;
              if (diagramCheckboxState[key]) {
                diagramCheckboxState[key].exclude = true;
              }
            });

            newDiagramCheckboxStates[diagramUuid] = diagramCheckboxState;

            // Initialize pagination states for this diagram
            newDiagramPaginationStates[diagramUuid] = {
              matched: {
                currentPageIndex: 1,
                itemsArray: [5, 10, 20, 50, 100],
                itemsPerPage: 10,
              },
              unmatched: {
                currentPageIndex: 1,
                itemsArray: [5, 10, 20, 50, 100],
                itemsPerPage: 10,
              },
            };
          }
        );
      }

      // Update all states
      setDiagramStates(newDiagramStates);
      setDiagramTechnologies(newDiagramTechnologies);
      setDiagramCheckboxStates(newDiagramCheckboxStates);
      setDiagramPaginationStates(newDiagramPaginationStates);
    } catch (err) {
      console.error("[LucidChart] Error loading per-diagram summaries:", err);
    }
  }, [assetId, dbName, containerName, selectedCloudProvider]);

  const fetchDiagrams = useCallback(async () => {
    if (!assetId) return;
    setIsLoading(true);
    try {
      const endpoint = `${apiEndpoint}&operation=get`;
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`,
          partitionKey: assetId,
        }),
      });

      if (!response.ok)
        throw new Error(`Error fetching data: ${response.status}`);

      const data = await response.json();
      const record = Array.isArray(data) ? data[0] : data;

      if (record && Array.isArray(record.arch_diags)) {
        setDiagrams(
          record.arch_diags.filter((d) => d.diagram_name && d.hyperlink)
        );
      } else {
        setDiagrams([]);
      }
    } catch (err) {
      console.error("Error fetching architecture diagrams:", err);
      setDiagrams([]);
    } finally {
      setIsLoading(false);
    }
  }, [assetId, apiEndpoint, dbName, containerName]);

  useEffect(() => {
    fetchDiagrams();
    fetchSummaryAndMatch();
  }, [fetchDiagrams, fetchSummaryAndMatch]);

  // Listen for sessionStorage changes to update provider
  useEffect(() => {
    const handleStorageChange = () => {
      const newProvider = sessionStorage.getItem(
        `selectedCloudProvider_${assetId}`
      );
      if (newProvider !== selectedCloudProvider) {
        setSelectedCloudProvider(newProvider);
      }
    };

    // Listen for storage events (changes from other tabs/components)
    window.addEventListener("storage", handleStorageChange);

    // Also poll for changes since storage events don't fire for same-tab changes
    const pollInterval = setInterval(handleStorageChange, 500);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(pollInterval);
    };
  }, [assetId, selectedCloudProvider]);

  // Re-process technologies when provider changes
  useEffect(() => {
    if (assetId) {
      fetchSummaryAndMatch();
    }
  }, [selectedCloudProvider, fetchSummaryAndMatch, assetId]);

  // Dialog handler functions
  const handleOpenDiagramDialog = () => setIsDiagramDialogOpen(true);
  const handleCloseDiagramDialog = () => setIsDiagramDialogOpen(false);

  // Handle adding new diagram
  const handleSubmitDiagram = async (formData) => {
    const newDiagramData = {
      diagram_name: formData?.diagramName,
      hyperlink: formData?.hyperlink,
    };

    try {
      // First get the existing document if it exists
      const getResponse = await fetch(`${apiEndpoint}&operation=get`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'architecture-details'`,
          partitionKey: assetId,
        }),
      });

      if (!getResponse.ok) {
        throw new Error(
          `Error getting existing data: ${getResponse.status} ${getResponse.statusText}`
        );
      }

      let existingData = await getResponse.json();

      // Handle if the response is an array
      if (Array.isArray(existingData) && existingData.length > 0) {
        existingData = existingData[0];
      }

      let documentData;

      // Handle document creation or update
      if (existingData && existingData.id === "architecture-details") {
        // Update existing document
        documentData = { ...existingData };

        // Initialize arch_diags if it doesn't exist
        if (!documentData.arch_diags) {
          documentData.arch_diags = [];
        }

        // Add the new diagram
        documentData.arch_diags.push(newDiagramData);
      } else {
        // Create new document
        documentData = {
          id: "architecture-details",
          asset_id: assetId,
          arch_diags: [newDiagramData],
          adrs: [],
          road_map: [],
          integrations: [],
          actors: [],
          comments: [],
        };
      }

      // Insert/update the document
      const insertResponse = await fetch(`${apiEndpoint}&operation=insert`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          dbName,
          containerName,
          jsonData: documentData,
          partitionKey: assetId,
        }),
      });

      if (!insertResponse.ok) {
        throw new Error(
          `Error saving diagram: ${insertResponse.status} ${insertResponse.statusText}`
        );
      }

      setAlertMessage("Diagram added successfully!");
      setOpenAlertOptions("success");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);

      // Close dialog and refresh diagrams
      setIsDiagramDialogOpen(false);
      await fetchDiagrams();
    } catch (err) {
      console.error(err);
      setAlertMessage("Failed to add diagram: " + err.message);
      setOpenAlertOptions("error");
      setTimeout(() => setOpenAlertOptions(undefined), 5000);
    }
  };

  const extractUuid = (hyperlink) => {
    const match = hyperlink.match(/lucidchart\/([a-f0-9-]{36})/);
    return match ? match[1] : null;
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "N/A";
    const date = new Date(timestamp);

    const dateStr = date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

    const timeStr = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZoneName: "short",
    });

    return `${dateStr} ${timeStr}`;
  };

  const handleAnalyseDiagram = async (diagram) => {
    const diagramUuid = extractUuid(diagram.hyperlink);
    if (!diagramUuid) {
      setAlertMessage(
        `No valid LucidChart UUID found in diagram link: ${diagram.diagram_name}`
      );
      setOpenAlertOptions("error");
      setTimeout(() => setOpenAlertOptions(undefined), 5000);
      return;
    }

    // Set analyzing state for this specific diagram
    setDiagramStates((prev) => ({
      ...prev,
      [diagramUuid]: {
        ...prev[diagramUuid],
        isAnalysing: true,
      },
    }));

    try {
      // Call Lucid API for this specific diagram
      const lucidResponse = await fetch(lucidEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ document_id: diagramUuid }),
      });

      if (!lucidResponse.ok)
        throw new Error(`Lucid API error: ${lucidResponse.status}`);

      const lucidData = await lucidResponse.json();

      // Get existing summary record or create new structure
      const existingSummaryData = await callMiddleware("get", {
        dbName,
        containerName,
        query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'lucid_mcp_tech_summary'`,
        partitionKey: assetId,
      });

      const existingRecord = Array.isArray(existingSummaryData)
        ? existingSummaryData[0]
        : existingSummaryData;

      // Build new per-diagram structure
      const diagramSummaries = existingRecord?.diagram_summaries || {};
      const timestamp = new Date().toISOString();

      // Update this diagram's summary
      diagramSummaries[diagramUuid] = {
        diagram_name: diagram.diagram_name,
        diagram_link: diagram.hyperlink,
        last_analyzed: timestamp,
        lucid_summary: lucidData,
      };

      // Save updated record
      await callMiddleware("insert", {
        dbName,
        containerName,
        jsonData: {
          id: "lucid_mcp_tech_summary",
          asset_id: assetId,
          diagram_summaries: diagramSummaries,
          last_modified: timestamp,
        },
        partitionKey: assetId,
      });

      setAlertMessage(
        `Analysis complete for "${diagram.diagram_name}". Summary saved successfully.`
      );
      setOpenAlertOptions("success");

      // Update diagram state with new timestamp
      setDiagramStates((prev) => ({
        ...prev,
        [diagramUuid]: {
          ...prev[diagramUuid],
          isAnalysing: false,
          lastAnalyzed: timestamp,
          expanded: true, // Auto-expand to show results
        },
      }));

      // Reload data for this diagram
      await fetchSummaryAndMatch();
    } catch (err) {
      console.error(`Analysis failed for ${diagram.diagram_name}:`, err);
      setAlertMessage(
        `Analysis failed for "${diagram.diagram_name}": ${err.message}`
      );
      setOpenAlertOptions("error");

      // Reset analyzing state
      setDiagramStates((prev) => ({
        ...prev,
        [diagramUuid]: {
          ...prev[diagramUuid],
          isAnalysing: false,
        },
      }));
    } finally {
      setTimeout(() => setOpenAlertOptions(undefined), 5000);
    }
  };

  // Toggle diagram expansion
  const toggleDiagramExpansion = (diagramUuid) => {
    setDiagramStates((prev) => ({
      ...prev,
      [diagramUuid]: {
        ...prev[diagramUuid],
        expanded: !prev[diagramUuid]?.expanded,
      },
    }));
  };

  // Modified checkbox behavior: allow multiple selections for Frontend/Backend/DevOps, but Exclude is mutually exclusive
  const handleCheckbox = (diagramUuid, key, field, checked) => {
    if (field === "exclude") {
      // Handle exclude separately with database update
      handleExcludeToggle(diagramUuid, key, checked);
      return;
    }

    setDiagramCheckboxStates((prev) => {
      const currentState = prev[diagramUuid]?.[key] || {
        frontend: false,
        backend: false,
        devops: false,
        exclude: false,
      };

      // If selecting any technology stack, clear exclude
      // If unchecking, preserve other selections
      return {
        ...prev,
        [diagramUuid]: {
          ...prev[diagramUuid],
          [key]: {
            ...currentState,
            [field]: checked,
            exclude: checked ? false : currentState.exclude, // Clear exclude if selecting any tech stack
          },
        },
      };
    });
  };

  // Save exclude status to lucid document (per-diagram)
  const handleExcludeToggle = async (diagramUuid, technologyKey, excluded) => {
    try {
      const lucidData = await callMiddleware("get", {
        dbName,
        containerName,
        query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'lucid_mcp_tech_summary'`,
        partitionKey: assetId,
      });

      const record = Array.isArray(lucidData) ? lucidData[0] : lucidData;
      if (
        !record ||
        !record.diagram_summaries ||
        !record.diagram_summaries[diagramUuid]
      ) {
        return;
      }

      const diagramData = record.diagram_summaries[diagramUuid];
      const technologiesData =
        diagramData?.lucid_summary?.combined_summary?.technologies;

      if (!technologiesData) return;

      // Initialize excluded array if it doesn't exist
      if (!technologiesData.excluded) {
        technologiesData.excluded = [];
      }

      let updated = false;

      if (excluded) {
        // Move technology TO excluded array
        const matchedIndex = technologiesData.matched_technologies?.findIndex(
          (tech) => (tech.service_id || tech.technology) === technologyKey
        );
        if (matchedIndex !== -1) {
          const techToMove = technologiesData.matched_technologies.splice(
            matchedIndex,
            1
          )[0];
          technologiesData.excluded.push(techToMove);
          updated = true;
        }

        const unmatchedIndex =
          technologiesData.unmatched_technologies?.findIndex(
            (tech) =>
              (tech.technology || tech.original_extracted) === technologyKey
          );
        if (unmatchedIndex !== -1) {
          const techToMove = technologiesData.unmatched_technologies.splice(
            unmatchedIndex,
            1
          )[0];
          technologiesData.excluded.push(techToMove);
          updated = true;
        }
      } else {
        // Move technology FROM excluded array back to its original array
        const excludedIndex = technologiesData.excluded.findIndex(
          (tech) =>
            (tech.service_id || tech.technology || tech.original_extracted) ===
            technologyKey
        );

        if (excludedIndex !== -1) {
          const techToMove = technologiesData.excluded.splice(
            excludedIndex,
            1
          )[0];

          if (techToMove.service_id) {
            if (!technologiesData.matched_technologies) {
              technologiesData.matched_technologies = [];
            }
            technologiesData.matched_technologies.push(techToMove);
          } else {
            if (!technologiesData.unmatched_technologies) {
              technologiesData.unmatched_technologies = [];
            }
            technologiesData.unmatched_technologies.push(techToMove);
          }
          updated = true;
        }
      }

      if (updated) {
        record.last_modified = new Date().toISOString();
        await callMiddleware("insert", {
          dbName,
          containerName,
          jsonData: record,
          partitionKey: assetId,
        });

        // Update local state immediately
        setDiagramCheckboxStates((prev) => ({
          ...prev,
          [diagramUuid]: {
            ...prev[diagramUuid],
            [technologyKey]: {
              ...prev[diagramUuid][technologyKey],
              exclude: excluded,
              frontend: false,
              backend: false,
              devops: false,
            },
          },
        }));

        // No need to refresh all data since we've updated local state and database
      }
    } catch (err) {
      console.error("[LucidChart] Error saving exclude status:", err);
      setAlertMessage(
        `Error ${excluded ? "excluding" : "restoring"} technology: ${
          err.message
        }`
      );
      setOpenAlertOptions("error");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);
    }
  };

  const handleAccept = (diagramUuid) => {
    setSelectedDiagramForImport(diagramUuid);
    setImportDialogOpen(true);
  };

  const handleConfirmImport = async () => {
    if (!selectedDiagramForImport) return;

    setImportDialogOpen(false);

    try {
      const diagramUuid = selectedDiagramForImport;
      const checkboxState = diagramCheckboxStates[diagramUuid] || {};
      const diagramTech = diagramTechnologies[diagramUuid] || {
        matchedTechnologies: [],
        unmatchedTechnologies: [],
      };

      // Get selected technologies with their stack types
      const technologiesToAdd = [];
      const allTechnologies = [
        ...diagramTech.matchedTechnologies,
        ...diagramTech.unmatchedTechnologies,
      ];

      Object.entries(checkboxState).forEach(([key, selections]) => {
        const tech = allTechnologies.find(
          (t) => (t.tech_radar_id || t.technologyName) === key
        );
        if (!tech) return;

        // Skip if excluded
        if (selections.exclude) return;

        // Add technology to each selected stack type
        if (selections.frontend) {
          technologiesToAdd.push({ tech, stackType: "frontend" });
        }
        if (selections.backend) {
          technologiesToAdd.push({ tech, stackType: "backend" });
        }
        if (selections.devops) {
          technologiesToAdd.push({ tech, stackType: "devops" });
        }
      });

      if (technologiesToAdd.length === 0) {
        setAlertMessage("No technologies selected for import.");
        setOpenAlertOptions("warning");
        setTimeout(() => setOpenAlertOptions(undefined), 3000);
        return;
      }

      // Fetch or create technology-stacks document
      const existingData = await callMiddleware("get", {
        dbName,
        containerName,
        query: `SELECT * FROM c WHERE c.asset_id = '${assetId}' AND c.id = 'technology-stacks'`,
        partitionKey: assetId,
      });

      let techStackData;
      if (
        existingData &&
        Array.isArray(existingData) &&
        existingData.length > 0
      ) {
        techStackData = existingData[0];
      } else if (existingData && existingData.id === "technology-stacks") {
        techStackData = existingData;
      } else {
        techStackData = {
          id: "technology-stacks",
          asset_id: assetId,
          technology_stacks: [],
          deviations: [],
          createdAt: new Date().toISOString(),
          last_modified: new Date().toISOString(),
        };
      }

      // Process each selected technology
      technologiesToAdd.forEach(({ tech, stackType }) => {
        const envValue = "Proposed";

        if (tech.tech_radar_id) {
          // Technology with service_id
          const service = {
            service_id: tech.tech_radar_id,
            pattern_ref_id: [],
          };

          let techStack = techStackData.technology_stacks?.find(
            (stack) => stack.tech_stack === stackType
          );

          if (!techStack) {
            techStack = {
              tech_stack: stackType,
              tech_stack_environment: [],
            };
            techStackData.technology_stacks =
              techStackData.technology_stacks || [];
            techStackData.technology_stacks.push(techStack);
          }

          let environment = techStack.tech_stack_environment?.find(
            (env) => env.environment === envValue
          );

          if (environment) {
            if (!environment.services && environment.service_ids) {
              environment.services = environment.service_ids.map((id) => ({
                service_id: id,
                pattern_ref_id: [],
              }));
              delete environment.service_ids;
            }

            if (!environment.services) {
              environment.services = [];
            }

            const existingService = environment.services.find(
              (s) => s.service_id === service.service_id
            );

            if (!existingService) {
              environment.services.push(service);
            }
          } else {
            techStack.tech_stack_environment =
              techStack.tech_stack_environment || [];
            techStack.tech_stack_environment.push({
              environment: envValue,
              score: "coming soon",
              services: [service],
            });
          }

          // Add to deviations if not Adopt or Mandate
          if (
            envValue === "Proposed" &&
            tech.recommendation !== "Adopt" &&
            tech.recommendation !== "Mandate"
          ) {
            if (!techStackData.deviations) {
              techStackData.deviations = [];
            }

            if (
              !techStackData.deviations.some(
                (d) => d.service_id === tech.tech_radar_id
              )
            ) {
              techStackData.deviations.push({
                service_id: tech.tech_radar_id,
                justification: "",
              });
            }
          }
        } else {
          // Technology without service_id (use service_name)
          const serviceName = tech.technologyName;
          if (!serviceName) return;

          const service = { service_name: serviceName };

          let techStack = techStackData.technology_stacks?.find(
            (stack) => stack.tech_stack === stackType
          );

          if (!techStack) {
            techStack = {
              tech_stack: stackType,
              tech_stack_environment: [],
            };
            techStackData.technology_stacks =
              techStackData.technology_stacks || [];
            techStackData.technology_stacks.push(techStack);
          }

          let environment = techStack.tech_stack_environment?.find(
            (env) => env.environment === envValue
          );

          if (environment) {
            if (!environment.services) {
              environment.services = [];
            }
            const exists = environment.services.some(
              (s) => s.service_name === serviceName
            );
            if (!exists) {
              environment.services.push(service);
            }
          } else {
            techStack.tech_stack_environment =
              techStack.tech_stack_environment || [];
            techStack.tech_stack_environment.push({
              environment: envValue,
              score: "coming soon",
              services: [service],
            });
          }

          if (envValue === "Proposed") {
            if (!techStackData.deviations) {
              techStackData.deviations = [];
            }

            if (
              !techStackData.deviations.some(
                (d) => d.service_name === serviceName
              )
            ) {
              techStackData.deviations.push({
                service_name: serviceName,
                justification: "",
              });
            }
          }
        }
      });

      techStackData.last_modified = new Date().toISOString();

      // Save updated technology-stacks document
      await callMiddleware("insert", {
        dbName,
        containerName,
        jsonData: techStackData,
        partitionKey: assetId,
      });

      // Count unique technologies (not total entries across stacks)
      const uniqueTechnologies = new Set(
        technologiesToAdd.map(
          (item) => item.tech.tech_radar_id || item.tech.technologyName
        )
      );

      setAlertMessage(
        `Successfully imported ${uniqueTechnologies.size} ${
          uniqueTechnologies.size === 1 ? "technology" : "technologies"
        }!`
      );
      setOpenAlertOptions("success");
      setTimeout(() => setOpenAlertOptions(undefined), 3000);

      // Clear selections for this diagram
      setDiagramCheckboxStates((prev) => ({
        ...prev,
        [diagramUuid]: Object.keys(prev[diagramUuid] || {}).reduce(
          (acc, key) => {
            acc[key] = {
              frontend: false,
              backend: false,
              devops: false,
              exclude: prev[diagramUuid][key].exclude, // Keep exclude state
            };
            return acc;
          },
          {}
        ),
      }));

      // Preserve expanded state before refresh
      const currentExpandedState = diagramStates[diagramUuid]?.expanded;

      // Refresh data
      await fetchSummaryAndMatch();

      // Restore expanded state after refresh
      if (currentExpandedState) {
        setDiagramStates((prev) => ({
          ...prev,
          [diagramUuid]: {
            ...prev[diagramUuid],
            expanded: true,
          },
        }));
      }
    } catch (err) {
      console.error("Error importing technologies:", err);
      setAlertMessage(`Failed to import technologies: ${err.message}`);
      setOpenAlertOptions("error");
      setTimeout(() => setOpenAlertOptions(undefined), 5000);
    }

    setSelectedDiagramForImport(null);
  };

  return (
    <div className="headers">
      {/* Global Alerts */}
      {openAlertOptions && (
        <SafAlert
          appearance={openAlertOptions}
          style={{ marginBottom: "20px" }}
          onClose={() => {
            setOpenAlertOptions(undefined);
            setAlertMessage("");
          }}
        >
          {alertMessage}
        </SafAlert>
      )}

      {/* Header Section */}
      <div
        style={{
          marginBottom: "24px",
          padding: "0 4px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "8px",
          }}
        >
          <SafText as="h3" style={{ margin: "0" }}>
            <strong>Architecture Diagrams</strong>
          </SafText>
          <SafButton
            appearance="primary"
            size="small"
            onClick={handleOpenDiagramDialog}
          >
            Add Diagram
          </SafButton>
        </div>
        <SafText className="text-muted" style={{ fontSize: "14px" }}>
          Analyze architecture diagrams individually to extract technology
          stacks
        </SafText>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: "100%" }}>
        {isLoading ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px",
              background: "#f9fafb",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
            }}
          >
            <SafText className="text-muted">Loading diagrams...</SafText>
          </div>
        ) : diagrams.length === 0 ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px",
              background: "#f9fafb",
              borderRadius: "12px",
              border: "1px solid #e5e7eb",
            }}
          >
            <SafText className="text-muted">
              No architecture diagrams available.
            </SafText>
          </div>
        ) : (
          <div
            style={{ display: "flex", flexDirection: "column", gap: "20px" }}
          >
            {diagrams.map((diagram) => {
              const diagramUuid = extractUuid(diagram.hyperlink) || "";
              // if (!diagramUuid) return null;

              const diagramState = diagramStates[diagramUuid] || {
                isAnalysing: false,
                lastAnalyzed: null,
                expanded: false,
              };
              const diagramTech = diagramTechnologies[diagramUuid] || {
                matchedTechnologies: [],
                unmatchedTechnologies: [],
              };
              const hasBeenAnalyzed =
                diagramTech.matchedTechnologies.length > 0 ||
                diagramTech.unmatchedTechnologies.length > 0;
              const checkboxState = diagramCheckboxStates[diagramUuid] || {};

              return (
                <div
                  key={diagramUuid}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "12px",
                    background: "#fff",
                    overflow: "hidden",
                    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
                    transition: "box-shadow 0.2s ease-in-out",
                  }}
                >
                  {/* Diagram Card Header */}
                  <div
                    style={{
                      padding: "20px 24px",
                      background: hasBeenAnalyzed ? "#f0f9ff" : "#fff",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Left: Diagram Info */}
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginBottom: "8px",
                          }}
                        >
                          <SafAnchor
                            href={diagram.hyperlink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              margin: "0",
                              fontWeight: "600",
                              fontSize: "1.25rem",
                              color: "#2563eb",
                              textDecoration: "none",
                            }}
                          >
                            {diagram.diagram_name}
                          </SafAnchor>
                          {diagramState.lastAnalyzed ? (
                            hasBeenAnalyzed ? (
                              <span
                                style={{
                                  padding: "2px 8px",
                                  background: "#dcfce7",
                                  color: "#166534",
                                  borderRadius: "12px",
                                  fontSize: "0.75rem",
                                  fontWeight: "500",
                                }}
                              >
                                Analyzed
                              </span>
                            ) : (
                              <span
                                style={{
                                  padding: "2px 8px",
                                  background: "#fef3c7",
                                  color: "#92400e",
                                  borderRadius: "12px",
                                  fontSize: "0.75rem",
                                  fontWeight: "500",
                                }}
                              >
                                No Technologies Found
                              </span>
                            )
                          ) : (
                            <span
                              style={{
                                padding: "2px 8px",
                                background: "#fef3c7",
                                color: "#92400e",
                                borderRadius: "12px",
                                fontSize: "0.75rem",
                                fontWeight: "500",
                              }}
                            >
                              Not Analyzed
                            </span>
                          )}
                        </div>

                        {/* Last Analyzed Timestamp */}
                        {diagramState.lastAnalyzed && (
                          <div style={{ marginTop: "8px" }}>
                            <SafText
                              className="text-muted"
                              style={{ fontSize: "0.8rem" }}
                            >
                              Last analyzed:{" "}
                              {formatDate(diagramState.lastAnalyzed)}
                            </SafText>
                          </div>
                        )}
                      </div>

                      {/* Right: Actions */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        {hasBeenAnalyzed && (
                          <SafButton
                            appearance="secondary"
                            onClick={() => toggleDiagramExpansion(diagramUuid)}
                            style={{
                              minWidth: "100px",
                              height: "36px",
                              fontSize: "0.875rem",
                            }}
                          >
                            {diagramState.expanded
                              ? "Hide Details"
                              : "Show Details"}
                          </SafButton>
                        )}
                        <SafButton
                          appearance="primary"
                          disabled={diagramState.isAnalysing}
                          onClick={() => handleAnalyseDiagram(diagram)}
                          style={{
                            minWidth: "100px",
                            height: "36px",
                            fontSize: "0.875rem",
                          }}
                        >
                          {diagramState.isAnalysing
                            ? "Analyzing..."
                            : "Analyze"}
                        </SafButton>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content - Technology Tables */}
                  {hasBeenAnalyzed && diagramState.expanded && (
                    <div style={{ padding: "20px 24px" }}>
                      {/* Matched Technologies Section */}
                      {diagramTech.matchedTechnologies.length > 0 && (
                        <div style={{ marginBottom: "24px" }}>
                          <SafText>
                            <strong>
                              Matched Technologies (From Tech Stack Mapping)
                            </strong>
                          </SafText>
                          <br />
                          <div style={{ marginTop: "10px" }}>
                            <SafTable style={{ width: "100%" }}>
                              <thead>
                                <tr>
                                  <th style={{ textAlign: "left" }}>
                                    Technology Name
                                  </th>
                                  <th style={{ textAlign: "left" }}>
                                    Category
                                  </th>
                                  <th style={{ textAlign: "left" }}>
                                    Capability
                                  </th>
                                  <th style={{ textAlign: "left" }}>
                                    Recommendation
                                  </th>
                                  <th style={{ textAlign: "left" }}>
                                    Provider
                                  </th>
                                  <th style={{ textAlign: "center" }}>
                                    Front-end
                                  </th>
                                  <th style={{ textAlign: "center" }}>
                                    Back-end
                                  </th>
                                  <th style={{ textAlign: "center" }}>
                                    DevOps
                                  </th>
                                  <th style={{ textAlign: "center" }}>
                                    Exclude
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {diagramTech.matchedTechnologies.map(
                                  (tech, idx) => {
                                    const key =
                                      tech.tech_radar_id || tech.technologyName;
                                    const state = checkboxState[key] || {
                                      frontend: false,
                                      backend: false,
                                      devops: false,
                                      exclude: false,
                                    };
                                    const isExcluded = state.exclude;
                                    return (
                                      <tr
                                        key={key || idx}
                                        style={
                                          isExcluded
                                            ? {
                                                opacity: 0.4,
                                                pointerEvents: "auto",
                                              }
                                            : {}
                                        }
                                      >
                                        <td>{tech.technologyName}</td>
                                        <td>{tech.category}</td>
                                        <td>{tech.capability}</td>
                                        <td>{tech.recommendation}</td>
                                        <td>{tech.provider}</td>
                                        <td style={{ textAlign: "center" }}>
                                          <SafCheckbox
                                            checked={state.frontend}
                                            onChange={(e) =>
                                              handleCheckbox(
                                                diagramUuid,
                                                key,
                                                "frontend",
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                          <SafCheckbox
                                            checked={state.backend}
                                            onChange={(e) =>
                                              handleCheckbox(
                                                diagramUuid,
                                                key,
                                                "backend",
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                          <SafCheckbox
                                            checked={state.devops}
                                            onChange={(e) =>
                                              handleCheckbox(
                                                diagramUuid,
                                                key,
                                                "devops",
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                          <SafCheckbox
                                            checked={state.exclude}
                                            onChange={(e) =>
                                              handleCheckbox(
                                                diagramUuid,
                                                key,
                                                "exclude",
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                      </tr>
                                    );
                                  }
                                )}
                              </tbody>
                            </SafTable>
                          </div>
                        </div>
                      )}

                      {/* Unmatched Technologies Section */}
                      {diagramTech.unmatchedTechnologies.length > 0 && (
                        <div style={{ marginBottom: "24px" }}>
                          <SafText>
                            <strong>
                              Unmatched Technologies (Not in Tech Stack Mapping)
                            </strong>
                          </SafText>
                          <br />
                          <div style={{ marginTop: "10px" }}>
                            <SafTable style={{ width: "100%" }}>
                              <thead>
                                <tr>
                                  <th style={{ textAlign: "left" }}>
                                    Technology Name
                                  </th>
                                  <th style={{ textAlign: "left" }}>
                                    Category
                                  </th>
                                  <th style={{ textAlign: "left" }}>
                                    Capability
                                  </th>
                                  <th style={{ textAlign: "left" }}>
                                    Recommendation
                                  </th>
                                  <th style={{ textAlign: "left" }}>
                                    Provider
                                  </th>
                                  <th style={{ textAlign: "center" }}>
                                    Front-end
                                  </th>
                                  <th style={{ textAlign: "center" }}>
                                    Back-end
                                  </th>
                                  <th style={{ textAlign: "center" }}>
                                    DevOps
                                  </th>
                                  <th style={{ textAlign: "center" }}>
                                    Exclude
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {diagramTech.unmatchedTechnologies.map(
                                  (tech, idx) => {
                                    const key = tech.technologyName;
                                    const state = checkboxState[key] || {
                                      frontend: false,
                                      backend: false,
                                      devops: false,
                                      exclude: false,
                                    };
                                    const isExcluded = state.exclude;
                                    return (
                                      <tr
                                        key={key || idx}
                                        style={
                                          isExcluded
                                            ? {
                                                opacity: 0.4,
                                                pointerEvents: "auto",
                                              }
                                            : {}
                                        }
                                      >
                                        <td>{tech.technologyName}</td>
                                        <td>{tech.category}</td>
                                        <td>{tech.capability}</td>
                                        <td>{tech.recommendation}</td>
                                        <td>{tech.provider}</td>
                                        <td style={{ textAlign: "center" }}>
                                          <SafCheckbox
                                            checked={state.frontend}
                                            onChange={(e) =>
                                              handleCheckbox(
                                                diagramUuid,
                                                key,
                                                "frontend",
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                          <SafCheckbox
                                            checked={state.backend}
                                            onChange={(e) =>
                                              handleCheckbox(
                                                diagramUuid,
                                                key,
                                                "backend",
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                          <SafCheckbox
                                            checked={state.devops}
                                            onChange={(e) =>
                                              handleCheckbox(
                                                diagramUuid,
                                                key,
                                                "devops",
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                        <td style={{ textAlign: "center" }}>
                                          <SafCheckbox
                                            checked={state.exclude}
                                            onChange={(e) =>
                                              handleCheckbox(
                                                diagramUuid,
                                                key,
                                                "exclude",
                                                e.target.checked
                                              )
                                            }
                                          />
                                        </td>
                                      </tr>
                                    );
                                  }
                                )}
                              </tbody>
                            </SafTable>
                          </div>
                        </div>
                      )}

                      {/* Import Actions - Show if either table has technologies */}
                      {(diagramTech.matchedTechnologies.length > 0 ||
                        diagramTech.unmatchedTechnologies.length > 0) && (
                        <div
                          style={{
                            marginTop: "24px",
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: "10px",
                          }}
                        >
                          <SafButton
                            appearance="secondary"
                            onClick={() => {
                              // Clear selections for this diagram
                              setDiagramCheckboxStates((prev) => ({
                                ...prev,
                                [diagramUuid]: Object.keys(
                                  prev[diagramUuid] || {}
                                ).reduce((acc, key) => {
                                  acc[key] = {
                                    frontend: false,
                                    backend: false,
                                    devops: false,
                                    exclude:
                                      prev[diagramUuid][key]?.exclude || false, // Keep exclude state
                                  };
                                  return acc;
                                }, {}),
                              }));
                            }}
                          >
                            Cancel
                          </SafButton>
                          <SafButton
                            appearance="primary"
                            onClick={() => handleAccept(diagramUuid)}
                          >
                            Accept
                          </SafButton>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Import Confirmation Dialog */}
      <SafDialog
        modal={false}
        is-alert={false}
        is-header={true}
        is-footer={true}
        no-focus-trap={false}
        dialog-title="Confirm Import"
        hidden={!importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
      >
        <div style={{ marginBottom: "16px" }}>
          Each technology will be added to its selected stack type(s). Do you
          want to proceed?
        </div>
        <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
          <SafButton
            appearance="secondary"
            onClick={() => setImportDialogOpen(false)}
          >
            Go Back
          </SafButton>
          <SafButton appearance="primary" onClick={handleConfirmImport}>
            Confirm Import
          </SafButton>
        </div>
      </SafDialog>

      {/* Add Diagram Dialog */}
      <SafDialogbox
        isOpen={isDiagramDialogOpen}
        handleOnClose={handleCloseDiagramDialog}
        handleCancelClick={handleCloseDiagramDialog}
        handleSubmit={handleSubmitDiagram}
        dialogTitle="Add Diagram"
      />
    </div>
  );
};

export default LucidChartImportTab;
